import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  IDefaultRoleQuery,
  IDefaultRoleQueryVariables,
  IRoleDefaultMutation,
  IRoleDefaultMutationVariables,
  IRoleMetaFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import {
  GET_DEFAULT_ROLE,
  ROLE_DEFAULT,
} from '../../../../utils/apollo/permission';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../../utils/toastMessage';
import BuilderSelectMenu from '../../../../components/Builder/BuilderSelectMenu';
import { assertNotNil } from '../../../../utils/types';

interface IProps {
  roles: IRoleMetaFieldsFragment[];
}

const DefaultRoleMenu = ({ roles }: IProps) => {
  const [defaultRoleId, setDefaultRoleId] = useState('');

  const handleLoadDefaultRole = ({ defaultRole }: IDefaultRoleQuery) => {
    assertNotNil(defaultRole);
    setDefaultRoleId(defaultRole.id);
  };
  const { loading } = useQuery<IDefaultRoleQuery, IDefaultRoleQueryVariables>(
    GET_DEFAULT_ROLE,
    {
      onCompleted: handleLoadDefaultRole,
    },
  );

  const [setDefaultRoleMutation] = useMutation<
    IRoleDefaultMutation,
    IRoleDefaultMutationVariables
  >(ROLE_DEFAULT);

  const handleChangeDefaultRole = async (selectedRoleId: string) => {
    const { data } = await setDefaultRoleMutation({
      variables: {
        input: {
          roleId: selectedRoleId,
        },
      },
    });

    if (printGraphqlErrors(data?.roleDefault?.errors)) {
      return;
    }

    setDefaultRoleId(selectedRoleId);

    const newRole = roles.find((role) => role.id === selectedRoleId);
    assertNotNil(newRole);

    toastMessage({
      message: `Default role has been changed to: ${newRole.name}.`,
      type: 'info',
    });
  };

  return (
    <BuilderSelectMenu
      label={'Default Role for New Users'}
      options={roles
        .filter((role) => role.active)
        .map((role) => ({
          text: role.name,
          value: role.id,
        }))}
      value={defaultRoleId}
      onChange={handleChangeDefaultRole}
      disabled={loading}
    />
  );
};

export default DefaultRoleMenu;
